import { cloneDeep } from "lodash";
import { QR_TYPES } from "@/constant/qr_type.enum";
import { UserSocialType } from "@/types/user.types";
import { createWithEqualityFn } from "zustand/traditional";
import { SOCIAL_ITEMS_DATA } from "./social-items.repository";
import { initialState, QRData } from "./initial-type.repository";
import { SOCIAL_TYPES } from "@/types/qr.types";
import { HoursType } from "@/types/all.types";

type State = {
  data: QRData;
  socialTypes: SOCIAL_TYPES[];
};

type Actions = {
  addOrUpdateData: (qr_type: QR_TYPES, payload: Record<string, any>) => void;
  setPayloadData: (qr_type: QR_TYPES, payload: Record<string, any>) => void;
  deleteData: (qr_type: QR_TYPES, key: string) => void;
  deleteFileState: (qr_type: QR_TYPES, key: string) => void;
  removeFileFromState: (qr_type: QR_TYPES, key: string, id: number) => void;
  removeFileFromPayload: (qr_type: QR_TYPES, key: string, id: number) => void;
  getData: (qr_type: QR_TYPES, key: string) => any;
  setReorderSocial: (val: UserSocialType[], qr_type: QR_TYPES) => void;
  setDeleteSocialItem: (index: number, qr_type: QR_TYPES) => void;
  setSocialItems: (
    val: any,
    qr_type: QR_TYPES,
    type: string,
    index?: number,
    action?: string,
  ) => void;
  setSocialTypes: (val: any) => void;
  addInitialSocialItems: (initialData: any, qr_type: QR_TYPES) => void;
  setShowUseBarCode: (val: boolean, qr_type: QR_TYPES) => void;
  setShowButton: (params: boolean, qr_type: QR_TYPES) => void;
  setShowMoreAddress: (params: boolean, qr_type: QR_TYPES) => void;
  setShowMoreContact: (params: boolean, qr_type: QR_TYPES) => void;
  setDeleteSocialTypes: (val: any, qr_type: QR_TYPES) => void;
  setAmenities: (params: any, qr_type: QR_TYPES) => void;
  setOpeningHours: (params: any, type: string, qr_type: QR_TYPES) => void;
  setChangeHoursType: (val: HoursType, qr_type: QR_TYPES) => void;
  setShowAllDay: (val: boolean, qr_type: QR_TYPES) => void;
  setImageCaptionUpdate: (params: { qr_type: QR_TYPES; index: number; caption: string }) => void;
  setReorderPayloadImages: (qr_type: QR_TYPES, params: any) => void;
  setReorderData: (qr_type: QR_TYPES, key: string, params: any) => void;
  setUserDeleteVideo: (val: number, qr_type: QR_TYPES) => void;
  setUserUpdateVideo: (val: { index: number; value: string }, qr_type: QR_TYPES) => void;
  deleteDataFromPayloadFile: (qr_type: QR_TYPES, key: string) => void;
  resetData: () => void;
};
const useQrPayloadStore = createWithEqualityFn<State & Actions>()((set, get) => ({
  data: initialState,
  socialTypes: SOCIAL_ITEMS_DATA,
  addInitialSocialItems: (initialData, qr_type) => {
    set((state) => ({
      socialItems: initialData,
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type].payload,
            socialItems: initialData,
          },
        },
      },
    }));
  },
  setShowUseBarCode: (val, qr_type) => {
    set((state) => ({
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type].payload,
            showUseBarCode: val,
          },
        },
      },
    }));
  },
  setShowAllDay: (val, qr_type) => {
    set((state) => ({
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type].payload,
            allDay: val,
          },
        },
      },
    }));
  },
  addOrUpdateData: (qr_type, payload) =>
    set((state) => ({
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          ...payload,
        },
      },
    })),
  setPayloadData: (qr_type, newPayload) => {
    set((state) => ({
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type].payload,
            ...newPayload,
          },
        },
      },
    }));
  },
  deleteData: (qr_type, key) =>
    set((state) => {
      const newData = { ...state.data[qr_type] };
      delete newData[key];
      return {
        data: {
          ...state.data,
          [qr_type]: newData,
        },
      };
    }),

  deleteFileState: (qr_type, key) =>
    set((state) => {
      const newData = { ...state.data[qr_type].files };
      delete newData[key];
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...state.data[qr_type],
            files: newData,
          },
        },
      };
    }),
  removeFileFromState: (qr_type, key, id) =>
    set((state) => {
      const data = state.data[qr_type];
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...data,
            files: {
              ...data.files,
              [key]: data.files[key].filter((item: any) => item.id !== id),
            },
          },
        },
      };
    }),
  removeFileFromPayload: (qr_type, key, id) =>
    set((state) => {
      const data = state.data[qr_type];

      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...data,
            payload: {
              ...data.payload,
              files: {
                ...data.payload.files,
                [key]: data.payload.files[key].filter((item: any) => item !== id),
              },
            },
          },
        },
      };
    }),
  deleteDataFromPayloadFile: (qr_type, key) =>
    set((state) => {
      const newData = { ...state.data[qr_type].payload?.files };
      delete newData[key];
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...state.data[qr_type],
            payload: {
              ...state.data[qr_type].payload,
              files: {
                ...newData,
              },
            },
          },
        },
      };
    }),

  getData: (qr_type, key) => get().data[qr_type][key],

  setReorderSocial: (val, qr_type) =>
    set((state) => ({
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type]?.payload,
            socialItems: val,
          },
        },
      },
    })),

  setDeleteSocialItem: (index, qr_type) =>
    set((state) => {
      const socialItems =
        state.data[qr_type]?.payload?.socialItems
          .filter((_: any, i: number) => i !== index)
          .map((item: UserSocialType, i: number) => ({
            ...item,
            id: i,
          })) ?? [];
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...state.data[qr_type],
            payload: {
              ...state.data[qr_type].payload,
              socialItems,
            },
          },
        },
      };
    }),

  setSocialItems: (val, qr_type, type, index, action) =>
    set((state) => {
      if (type === "add") {
        return {
          data: {
            ...state.data,
            [qr_type]: {
              ...state.data[qr_type],
              payload: {
                ...state.data[qr_type].payload,
                socialItems: [...state.data[qr_type]?.payload?.socialItems, val],
              },
            },
          },
        };
      }
      if (type === "update_value" && action !== undefined && index !== undefined) {
        const socialItems = [...(state.data[qr_type]?.payload?.socialItems || [])];
        (socialItems[index] as any)[action] = val;
        return {
          data: {
            ...state.data,
            [qr_type]: {
              ...state.data[qr_type],
              payload: {
                ...state.data[qr_type].payload,
                socialItems,
              },
            },
          },
        };
      }
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...state.data[qr_type],
            payload: {
              ...state.data[qr_type].payload,
              socialItems: val,
            },
          },
        },
      };
    }),

  setSocialTypes: (val) =>
    set((state) => ({
      socialTypes: state.socialTypes.map((item) =>
        item.type.toUpperCase().replace(/\s+/g, "") === val.type.toUpperCase().replace(/\s+/g, "")
          ? { ...item, active: val.active }
          : item,
      ),
    })),
  setShowButton: (params, qr_type) =>
    set((state) => ({
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type].payload,
            showButton: params,
          },
        },
      },
    })),

  setDeleteSocialTypes: (val, qr_type) =>
    set((state) => {
      const socialItems =
        state.data[qr_type]?.payload?.socialItems.filter(
          (item: UserSocialType) =>
            item.type.toUpperCase().replace(/\s+/g, "") !==
            val.type.toUpperCase().replace(/\s+/g, ""),
        ) ?? [];
      const data = { ...state.data };
      delete data[qr_type];
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...state.data[qr_type],
            payload: {
              ...state.data[qr_type].payload,
              socialItems,
            },
          },
        },
      };
    }),

  setChangeHoursType: (val, qr_type) => {
    set((state) => {
      if (val === "24h") {
        state.data[qr_type]?.payload?.opening_hours?.forEach((item: any) => {
          item.hours?.forEach((_hours: any) => {
            const currentStart = _hours?.start;
            const currentEnd = _hours?.end;

            _hours.start = "00:00";
            _hours.end = "23:59";

            if (currentStart) {
              _hours.start = "00:00";
            }
            if (currentEnd) {
              _hours.end = "23:59";
            }
          });
        });
      } else {
        state.data[qr_type]?.payload?.opening_hours?.forEach((item: any) => {
          item.hours?.forEach((_hours: any) => {
            const currentStart = _hours?.start;
            const currentEnd = _hours?.end;

            _hours.start = "12:00 am";
            _hours.end = "12:00 pm";

            if (currentStart) {
              _hours.start = "12:00 am";
            }
            if (currentEnd) {
              _hours.end = "12:00 pm";
            }
          });
        });
      }

      return { ...state };
    });
  },

  setOpeningHours: (val, type, qr_type) => {
    set((state) => {
      if (type === "fromItems") {
        if (val.hours.start !== undefined) {
          state.data[qr_type].payload.opening_hours[val.dayIndex].hours[val.hoursIndex].start =
            val.hours.start;
        } else if (val.hours.end !== undefined) {
          state.data[qr_type].payload.opening_hours[val.dayIndex].hours[val.hoursIndex].end =
            val.hours.end;
        }
      } else if (type === "fromSwitch") {
        state.data[qr_type].payload.opening_hours[val.parentIndex].active = val.active;
      } else if (type === "fromAddButton") {
        if (state.data[qr_type].payload.opening_hours[val.parentIndex].hours.length < 2) {
          state.data[qr_type].payload.opening_hours[val.parentIndex].hours.push(val.hours);
        }
      } else if (type === "fromDeleteButton") {
        state.data[qr_type].payload.opening_hours[val]?.hours.splice(1, 1);
      }

      return { ...state };
    });
  },

  setShowMoreAddress: (val, qr_type) => {
    set((state) => ({
      ...state,
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type].payload,
            showMoreAddress: val,
            address: val ? "" : state.data[qr_type].payload.address,
          },
        },
      },
    }));
  },

  setShowMoreContact: (val, qr_type) => {
    set((state) => ({
      ...state,
      data: {
        ...state.data,
        [qr_type]: {
          ...state.data[qr_type],
          payload: {
            ...state.data[qr_type].payload,
            showMoreContact: val,
          },
        },
      },
    }));
  },

  setAmenities: (val, qr_type) => {
    set((state) => {
      const existingAmenityIndex = state.data[qr_type]?.payload?.venue_amenities?.findIndex(
        (e: any) => e.type === val.type,
      );
      if (existingAmenityIndex === -1) {
        return {
          ...state,
          data: {
            ...state.data,
            [qr_type]: {
              ...state.data[qr_type],
              payload: {
                ...state.data[qr_type].payload,
                venue_amenities: [...(state.data[qr_type]?.payload?.venue_amenities || []), val],
              },
            },
          },
        };
      } else {
        const updatedAmenities = state.data[qr_type]?.payload?.venue_amenities?.filter(
          (e: any) => e.type !== val.type,
        );
        return {
          ...state,
          data: {
            ...state.data,
            [qr_type]: {
              ...state.data[qr_type],
              payload: {
                ...state.data[qr_type].payload,
                venue_amenities: updatedAmenities,
              },
            },
          },
        };
      }
    });
  },
  setImageCaptionUpdate: ({ qr_type, index, caption }) => {
    set((state) => {
      const qrData = state.data[qr_type];
      if (qrData && qrData.images && qrData.images[index]) {
        qrData.images[index].captions = caption;
        return {
          data: {
            ...state.data,
            [qr_type]: {
              ...qrData,
              payload: {
                ...qrData.payload,
                images: [...qrData.images],
              },
            },
          },
        };
      }
      return state;
    });
  },
  setReorderPayloadImages: (qr_type, val) => {
    set((state) => {
      const imageIds = val.map((item: any) => item.id);
      const qrData = state.data[qr_type];
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...qrData,
            payload: {
              ...qrData.payload,
              files: {
                ...qrData.files,
                welcome_screen: qrData.files.welcome_screen?.id || null,
                images: [...imageIds],
              },
            },
          },
        },
      };
    });
  },

  setReorderData(qr_type, key, params) {
    set((state) => {
      const qrData = state.data[qr_type];
      return {
        data: {
          ...state.data,
          [qr_type]: {
            ...qrData,
            payload: {
              ...qrData.payload,
              [key]: [...params],
            },
          },
        },
      };
    });
  },
  setUserDeleteVideo: (index) => {
    set((state) => {
      const videos = state.data[QR_TYPES.VIDEO]?.payload?.videos ?? [];
      if (index >= 0 && index < videos.length) {
        videos.splice(index, 1);
        return {
          data: {
            ...state.data,
            [QR_TYPES.VIDEO]: {
              ...state.data[QR_TYPES.VIDEO],
              payload: {
                ...state.data[QR_TYPES.VIDEO].payload,
                videos: [...videos],
              },
            },
          },
        };
      }
      return state;
    });
  },
  setUserUpdateVideo: (val) => {
    set((state) => {
      const { index, value } = val;
      const videos =
        state.data[QR_TYPES.VIDEO]?.payload?.videos?.map(
          (obj: { name: string }, _index: number) => {
            if (_index === index) {
              obj.name = value;
            }
            return obj;
          },
        ) ?? [];
      return {
        data: {
          ...state.data,
          [QR_TYPES.VIDEO]: {
            ...state.data[QR_TYPES.VIDEO],
            payload: {
              ...state.data[QR_TYPES.VIDEO].payload,
              videos,
            },
          },
        },
      };
    });
  },
  resetData: () =>
    set(() => {
      const clonedData = cloneDeep(initialState);
      return { data: { ...clonedData } };
    }),
}));

export default useQrPayloadStore;
